import {
  Access,
  CreateAccessPayload,
} from '../domain/shared-accounts/model';
import { SharedAccountsRepositoryImplementation } from '../infrastructure/shared-accounts-repository-implementation';
import { lastValueFrom } from 'rxjs';
import { cacheManager } from '../infrastructure/cache-manager';
import { Invitation } from '../types/accountant';

const CACHE_PREFIX = 'shared_accounts';

// Accesos
async function getAccesses(uid: string, cid: string, page: number, limit: number): Promise<Access[]> {
  const cacheKey = `${CACHE_PREFIX}-list-${page}-${limit}-${uid}-${cid}`;
  const cached = cacheManager.getItem<Access[]>(cacheKey);

  if (cached) {
    return cached;
  }

  const repo = new SharedAccountsRepositoryImplementation(uid, cid);
  const response = await lastValueFrom(repo.getAccesses(page, limit));
  if (!response || !response.success || !response.data) return [];

  const data = response.data.data;
  cacheManager.setItem(cacheKey, data);
  return data;
}

async function getGrantedAccesses(uid: string, cid: string, page: number, limit: number): Promise<Access[]> {
  const cacheKey = `${CACHE_PREFIX}-granted-list-${page}-${limit}-${uid}-${cid}`;
  const cached = cacheManager.getItem<Access[]>(cacheKey);

  if (cached) {
    return cached;
  }

  const repo = new SharedAccountsRepositoryImplementation(uid, cid);
  const response = await lastValueFrom(repo.getGrantedAccesses(page, limit));
  if (!response || !response.success || !response.data) return [];

  const data = response.data.data;
  cacheManager.setItem(cacheKey, data);
  return data;
}

async function createAccess(uid: string, cid: string, access: CreateAccessPayload): Promise<Access> {
  const repo = new SharedAccountsRepositoryImplementation(uid, cid);
  const response = await lastValueFrom(repo.createAccess(access));
  // Invalidar cachés después de una creación
  cacheManager.invalidate(CACHE_PREFIX);
  if (!response || !response.success) throw new Error('Failed to create access');
  return response.data;
}

async function deleteAccess(uid: string, cid: string, id: number): Promise<void> {
  const repo = new SharedAccountsRepositoryImplementation(uid, cid);
  await lastValueFrom(repo.deleteAccess(id));
  // Invalidar cachés después de una eliminación
  cacheManager.invalidate(CACHE_PREFIX);
}

async function createCollaborator(uid: string, cid: string, payload: { name: string, email: string }): Promise<Access> {
  const repo = new SharedAccountsRepositoryImplementation(uid, cid);
  const response = await lastValueFrom(repo.createCollaborator(payload));
  cacheManager.invalidate(CACHE_PREFIX);
  if (!response || !response.success) throw new Error('Failed to create collaborator');
  return response.data;
}

async function inviteAccountant(uid: string, cid: string, email: string): Promise<Invitation> {
  const repo = new SharedAccountsRepositoryImplementation(uid, cid);
  const response = await lastValueFrom(repo.inviteAccountant(email));
  cacheManager.invalidate(CACHE_PREFIX);
  if (!response || !response.success) throw new Error('Failed to invite accountant');
  return response.data;
}

export {
  getAccesses,
  createAccess,
  deleteAccess,
  getGrantedAccesses,
  createCollaborator,
  inviteAccountant
};
